import React, { useRef } from 'react';
import {
  Pressable,
  Text,
  StyleSheet,
  ActivityIndicator,
  Animated,
  ViewStyle,
  Platform,
} from 'react-native';
import { Colors } from '@constants/colors';

interface ButtonProps {
  title: string;
  onPress: () => void;
  variant?: 'primary' | 'secondary' | 'ai';
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  title,
  onPress,
  variant = 'primary',
  loading = false,
  disabled = false,
  fullWidth = true,
}) => {
  const scaleAnim = useRef(new Animated.Value(1)).current;

  const handlePressIn = () => {
    Animated.spring(scaleAnim, {
      toValue: 0.98,
      useNativeDriver: true,
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scaleAnim, {
      toValue: 1,
      friction: 3,
      tension: 40,
      useNativeDriver: true,
    }).start();
  };

  const getButtonStyle = (): ViewStyle => {
    const baseStyle: ViewStyle = {
      height: 56,
      borderRadius: 12,
      paddingHorizontal: 24,
      justifyContent: 'center',
      alignItems: 'center',
    };

    if (variant === 'primary') {
      return {
        ...baseStyle,
        backgroundColor: Colors.accent.primary,
        ...Platform.select({
          ios: {
            shadowColor: Colors.shadow.medium,
            shadowOffset: { width: 0, height: 5 },
            shadowOpacity: 1,
            shadowRadius: 6,
          },
          android: {
            elevation: 5,
          },
        }),
      };
    }

    if (variant === 'secondary') {
      return {
        ...baseStyle,
        backgroundColor: Colors.background.secondary,
        borderWidth: 2,
        borderColor: Colors.border.default,
        ...Platform.select({
          ios: {
            shadowColor: Colors.shadow.small,
            shadowOffset: { width: 0, height: 3 },
            shadowOpacity: 1,
            shadowRadius: 4,
          },
          android: {
            elevation: 3,
          },
        }),
      };
    }

    // AI variant
    return {
      ...baseStyle,
      backgroundColor: Colors.ai.primary,
      ...Platform.select({
        ios: {
          shadowColor: Colors.ai.primary,
          shadowOffset: { width: 0, height: 5 },
          shadowOpacity: 0.4,
          shadowRadius: 8,
        },
        android: {
          elevation: 5,
        },
      }),
    };
  };

  const getTextColor = (): string => {
    if (variant === 'primary') return '#FFFFFF';
    if (variant === 'secondary') return Colors.text.primary;
    return Colors.background.primary; // Dark text on cyan for AI variant
  };

  const containerStyle = [
    getButtonStyle(),
    fullWidth && styles.fullWidth,
    (disabled || loading) && styles.disabled,
  ];

  return (
    <Animated.View style={[{ transform: [{ scale: scaleAnim }] }, fullWidth && styles.fullWidth]}>
      <Pressable
        onPress={onPress}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        disabled={disabled || loading}
        style={containerStyle}
      >
        {loading ? (
          <ActivityIndicator color={getTextColor()} />
        ) : (
          <Text style={[styles.text, { color: getTextColor() }]}>{title}</Text>
        )}
      </Pressable>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  fullWidth: {
    width: '100%',
  },
  disabled: {
    opacity: 0.5,
  },
  text: {
    fontSize: 16,
    fontWeight: '600',
  },
});
